import { Component, inject, TemplateRef, ViewChild } from '@angular/core';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { TimesheetFilterComponent } from '../timesheet-filter/timesheet-filter.component';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LookupService } from '../../../../services/lookup.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { ListService } from '../../../../shared/services/list.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { ListTableComponent } from '@ppl/ui';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { PopupDialogComponent } from '@ppl/ui';
import { CommonModule } from '@angular/common';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { DatePickerModule } from 'primeng/datepicker';
import { UserService } from '../../../../services/user.service';
import CommonUtil from '../../../../shared/authorization/common.util';
import { TimesheetAdjustVoidEntryComponent } from '../timesheet-adjust-void-entry/timesheet-adjust-void-entry.component';
import { environment } from '../../../../../environments/environment';
import { AuthorizationDetailsComponent } from '../../authorizations/authorization-details/authorization-details.component';
import { SectionsService } from '../../../../services/sections.service';

@Component({
  selector: 'ppl-timesheet-payroll-adjustment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgZorroModule,
    NzDatePickerModule,
    DatePickerModule,
    ListTableComponent,
    TimesheetFilterComponent,
    TimesheetAdjustVoidEntryComponent,
    AuthorizationDetailsComponent
  ],
  templateUrl: './timesheet-payroll-adjustment.component.html',
  styleUrl: './timesheet-payroll-adjustment.component.css'
})
export class TimesheetPayrollAdjustmentComponent {
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild(TimesheetFilterComponent) timesheetFilterChildComponent!: TimesheetFilterComponent;
  @ViewChild('pendedDialog') pendedDialog!: TemplateRef<any>;
  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;
  @ViewChild('timesheetAdjustVoidDialog') timesheetAdjustVoidDialog!: TemplateRef<any>;
  @ViewChild('searchFilterWarningDialog') searchFilterWarningDialog!: TemplateRef<any>;
  @ViewChild('authDialog') authDialog!: TemplateRef<any>;
  // Passed to ppl-authorization-details as an input; never put into the shared selected lead.
  selectedAuthorizationId: any = null;

  private dialogSvc = inject(DialogService);
  private allowListStore = inject(AllowListAuthStore);

  pageId = PAGE_CONSTANTS.STAFF_PAYROLL_ADJUSTMENT;
  isReadOnly = false;
  actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
  appliedFilters: any;
  selectedAction: any;
  programName!: string;
  processingStatusList: any;
  businessStatusList: any;
  timesheets: any[] = [];
  hasSearched = false;
  // Toggled to destroy + recreate the results table for a clean full refresh on search.
  listReloading = false;
  serviceCodes: LookupValueItem[] = [];
  timesheetData: any = {};
  adjustVoidDialog: any;
  chequeNumbers: any[] = [];

  // Penalty service codes can only be voided, never adjusted.
  penaltyServiceCodes = ['CA_MEAL_PENALTY', 'REST_PENALTY'];

  buttons = [
    {
      button_id: "Adjust",
      button_label: "Adjust",
      buttonAction: "adjustTimesheet",
      className: "primary-btn-color"
    },
    // {
    //   button_id: "AdjustAll",
    //   button_label: "Adjust All",
    //   buttonAction: "adjustAllTimesheet",
    //   className: "primary-btn-color"
    // },
    {
      button_id: "Void",
      button_label: "Void",
      buttonAction: "voidTimesheet",
      className: "primary-btn-color"
    }
    // {
    //   button_id: "VoidAll",
    //   button_label: "Void All",
    //   buttonAction: "voidAllTimesheets",
    //   className: "primary-btn-color"
    // }
  ]

  cols = [
    {
      field: 'serviceDate',
      header: 'Service \nDate',
      jsonpath: 'serviceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'serviceDescription',
      header: 'Service \nCode',
      jsonpath: 'serviceDescription',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'authDesc',
      header: 'Service \nDescription',
      jsonpath: 'authDesc',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumerName',
      header: 'Participant \nName',
      jsonpath: 'consumerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumerPplId',
      header: 'Participant \nPPL ID',
      jsonpath: 'consumerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true
    },
    {
      field: 'providerName',
      header: 'Provider \nName',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerPplId',
      header: 'Provider \nPPL ID',
      jsonpath: 'providerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true
    },
    {
      field: 'timeInTimeOut',
      header: 'Time In - \nTime Out',
      jsonpath: 'timeInTimeOut',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'clockInLoc',
      header: 'Clock In \nLocation',
      jsonpath: 'clockInLoc',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'clockOutLoc',
      header: 'Clock Out \nLocation',
      jsonpath: 'clockOutLoc',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'billedUnitsInHrs',
      header: 'Billed \nHours',
      jsonpath: 'billedUnitsInHrs',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'paidUnits',
      header: 'Paid \nHours',
      jsonpath: 'paidUnits',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'serviceRate',
      header: 'Service \nRate',
      jsonpath: 'serviceRate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'businessStatus',
      header: 'Business \nStatus',
      jsonpath: 'businessStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'processingStatus',
      header: 'Processing \nStatus',
      jsonpath: 'processingStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'source_type_lkp',
      header: 'Timesheet \nSource',
      jsonpath: 'source_type_lkp',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'mco_name',
      header: 'Regional \nCenter',
      jsonpath: 'mco_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'tasAuthorizationDetailId',
      header: 'Authorization \nNumber',
      jsonpath: 'tasAuthorizationDetailId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true,
    },
    {
      field: 'associationStatus',
      header: 'CBA \nAssociation',
      jsonpath: 'associationStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  fields = [
    { field: 'Timeframe', show: true, default: '30 days' },
    { field: 'consumer', show: true },
    { field: 'consumerPplId', show: true },
    { field: 'provider', show: true },
    { field: 'providerPplId', show: true },
    { field: 'servicePeriod', show: true, hideFuturePeriods: true },
    { field: 'payPeriod', show: true, hideFuturePeriods: true },
    { field: 'serviceCode', show: true },
    { field: 'chequeNumber', show: true },
    { field: 'serviceDate', show: true, hideFuturePeriods: true },
  ];

  // Handler for list-table view user events (clicking PPL IDs)
  onViewUserClicked = (payload: any) => {
    this.userService.setViewUserEvent({
      userId: payload.userId,
      isConsumer: payload.isConsumer
    });
  }

  keyMapping: { [key: string]: string } = {
    serviceDate: "serviceDate",
    Timeframe: "serviceDate",
    payPeriod: "tasMasterPayrollScheduleId",
    consumer: "consumerName",
    consumerPplId: "consumerPplId",
    provider: "providerName",
    providerPplId: "providerPplId",
    chequeNumber: "chequeNumber",
    servicePeriod: "servicePeriodId",
    serviceCode: "serviceCodeDetailId"
  };

  constructor(
    private listService: ListService,
    private fb: FormBuilder,
    private lookupService: LookupService,
    private dateTimeFormatterService: DateTimeFormatterService,
    private userService: UserService,
    private errorAlertService: ErrorAlertService,
    private sectionsService: SectionsService
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.businessStatusList = this.lookupService.getLookupValue('Business Status');
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');
    this.setPageAccess();
  }

  private setPageAccess() {
    const authConfig = this.allowListStore.getConfig();
    if (authConfig && !authConfig.pages[this.pageId]) {
      this.isReadOnly = true;
    }
  }

  searchTimesheets(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false, recordsPerPage = 20) {
    this.hasSearched = true;

    const processedPayload = this.buildRequestPayload(
      'staffEditTimesheetEntry',
      'serviceDate',

      filters,
      startingPageNumber,
      recordsPerPage
    );

    this.listService
      .getListData(processedPayload, 'claims')
      .subscribe((data: any) => {
        const records = this.processData(data.responsedata);
        if (isChequeLookUp) {
          if (records && records?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
        else {
          if (startingPageNumber > 1) {
            this.timesheets = [...this.timesheets, ...records];
          } else {
            this.timesheets = records;
          }

          if (this.timesheets?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
      });
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20) {
    if (searchParams.length == 0) {
      return;
    }

    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };

    return payload;
  }

  processData(data: any) {
    if (data && data.length > 0) {
      data.forEach((element: any) => {
        element.timeInTimeOut = this.dateTimeFormatterService.convertTo12HourFormat(element.timeIn) + ' - ' + this.dateTimeFormatterService.convertTo12HourFormat(element.timeOut),
          element.businessStatus = this.businessStatusList.filter((status: any) => status.lkpValueCode === element.businessStatusLkp)
            .map((status: any) => status.lkpValueName),
          element.processingStatus = this.processingStatusList.filter((status: any) => status.lkpValueCode === element.processingStatusLkp)
            .map((status: any) => status.lkpValueName)

        const inTime = this.dateTimeFormatterService.formatStringToTime(element.timeIn);
        const outTime = this.dateTimeFormatterService.formatStringToTime(element.timeOut);
        if (inTime && outTime) {
          element.billedUnitsInHrs = this.dateTimeFormatterService.getDuration(inTime, outTime);
        } else {
          element.billedUnitsInHrs = element.billedUnits ? this.dateTimeFormatterService.formatUnitsToHoursMinutes(element.billedUnits * 15) : null;
        }
        element.paidUnits = element.paidUnits ? this.dateTimeFormatterService.formatUnitsToHoursMinutes(element.paidUnits) : null;

        // OTE entries are copied from the parent timesheet, so their time in/out and
        // clock locations carry no real meaning. Show them as '-' in the grid.
        if (CommonUtil.isOvertimeRow(element)) {
          element.timeInTimeOut = '-';
          element.clockInLoc = '-';
          element.clockOutLoc = '-';
        }
      });
    }

    return data;
  }

  private timeframeToDateString(value: string): string {
    const days = parseInt(value, 10);
    const date = new Date();
    date.setDate(date.getDate() - (isNaN(days) ? 30 : days));
    return date.toLocaleDateString('en-US');
  }

  onSearch(event: any) {
    const filters = [];

    if (event.Timeframe) {
      filters.push({
        jsonPath: "serviceDate",
        value: this.timeframeToDateString(event.Timeframe),
        operator: "greaterThan"
      });
    }

    if (event.serviceDate) {
      filters.push({
        jsonPath: "serviceDate",
        value: this.dateTimeFormatterService.formatDateToString(event.serviceDate as Date),
        operator: "Equal"
      });
    }

    if (event.payPeriod && event.payPeriod.length > 0) {
      filters.push({
        jsonPath: "tasMasterPayrollScheduleId",
        values: event.payPeriod,
        operator: "In"
      });
    }

    if (event.consumer && event.consumer.trim()) {
      filters.push({
        jsonPath: "consumerName",
        value: event.consumer.trim(),
        operator: "equal"
      });
    }

    if (event.consumerPplId && event.consumerPplId.trim()) {
      filters.push({
        jsonPath: "consumerPplId",
        value: event.consumerPplId.trim(),
        operator: "Equal"
      });
    }

    if (event.provider && event.provider.trim()) {
      filters.push({
        jsonPath: "providerName",
        value: event.provider.trim(),
        operator: "equal"
      });
    }

    if (event.providerPplId && event.providerPplId.trim()) {
      filters.push({
        jsonPath: "providerPplId",
        value: event.providerPplId.trim(),
        operator: "Equal"
      });
    }

    if (event.chequeNumber) {
      // Normalize the input — array, single string, or comma-separated string — into a clean string[].
      const chequeValues: string[] = Array.isArray(event.chequeNumber)
        ? event.chequeNumber.map((v: any) => String(v).trim()).filter(Boolean)
        : String(event.chequeNumber).split(',').map(v => v.trim()).filter(Boolean);

      if (chequeValues.length) {
        filters.push({
          jsonPath: "chequeNumber",
          values: chequeValues,
          operator: "In"
        });
      }
    }

    if (event.payPeriod) {
      filters.push({
        jsonPath: "tasMasterPayrollScheduleId",
        value: event.payPeriod,
        operator: "Equal"
      });
    }

    if (event.serviceCode) {
      filters.push({
        jsonPath: "serviceCodeDetailId",
        value: event.serviceCode,
        operator: "Equal"
      });
    }

    if (event.servicePeriod) {
      filters.push(
        { 
          jsonPath: 'servicePeriodId',
          value: event.servicePeriod,
          operator: 'Equal' 
        });
    }

    filters.push({
      jsonPath: "businessStatusLkp",
      value: 'P',
      operator: "Equal"
    });

    const hasRelevantFilter = filters.some(filter =>
      ["providerName", "consumerName", "providerPplId", "consumerPplId"].includes(filter.jsonPath)
    );


    this.appliedFilters = filters;
    this.reloadGrid(filters);
  }

  // Destroys + recreates the results table so the lazy loadPage grid fully resets to the
  // latest filter results, then reloads.
  private reloadGrid(filters: any) {
    this.listReloading = true;
    this.timesheets = [];
    setTimeout(() => {
      this.listReloading = false;
      this.searchTimesheets(filters);
    });
  }

  openWarningMessage() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      showOkButton: true,
      okText: 'Ok',
      size: 'medium',
      position: 'top',
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.searchFilterWarningDialog,
      dialogConfig,
      () => { },
      () => { }
    );
  }

  convertTimeStringToDate(timeString: string): Date {
    const currentDate = new Date();
    const [hours, minutes] = timeString.split(':');
    currentDate.setHours(Number(hours), Number(minutes), 0, 0);
    return currentDate;
  }

  areArraysEqual(a: any[], b: any[]): boolean {
    if (!Array.isArray(a) || !Array.isArray(b))
      return false;

    if (a.length !== b.length)
      return false;

    return a.every((val, i) => val === b[i]);
  }

  isFilterValid(timesheetFilterValues: any, searchCallbackFilterValues: any) {
    const activeFilters = Object.keys(timesheetFilterValues).filter(key => {
      const value = timesheetFilterValues[key];
      if (value === null || value === undefined)
        return false;

      if (typeof value === 'string' && value.trim() === '')
        return false;

      if (Array.isArray(value) && value.length === 0)
        return false;

      return true;
    });

    const userAppliedFilters = searchCallbackFilterValues.filter(
      (filter: any) => !['businessStatusLkp', 'serviceDuration'].includes(filter.jsonPath)
    );

    if (activeFilters.length !== userAppliedFilters.length)
      return false;

    return Object.entries(timesheetFilterValues)
      .filter(([_, value]) => value !== null && value !== "")
      .every(([key, value]: [any, any]) => {
        const mappedKey = this.keyMapping[key] || key;

        if (Array.isArray(value) && value.length === 0)
          return true;

        let comparisonValue: any;

        if (key === 'Timeframe') {
          comparisonValue = this.timeframeToDateString(value as string);
        }
        else if (mappedKey === 'serviceDate') {
          comparisonValue = this.dateTimeFormatterService.formatDateToString(value as Date);
        }
        else if (mappedKey === 'serviceDuration') {
          comparisonValue = value.substr(0, value.indexOf(' '));
        }
        // Check Number is a free-text field but is applied as an array (values/In),
        // so normalize the same way onSearch does to compare array-to-array.
        else if (mappedKey === 'chequeNumber') {
          comparisonValue = Array.isArray(value)
            ? value.map((v: any) => String(v).trim()).filter(Boolean)
            : String(value).split(',').map((v: string) => v.trim()).filter(Boolean);
        } else {
          comparisonValue = value;
        };

        return searchCallbackFilterValues.some((filter: any) => filter.jsonPath === mappedKey &&
          (Array.isArray(filter.value || filter.values) ? this.areArraysEqual(filter.values, comparisonValue) :
            typeof filter?.value === 'string' ? filter?.value?.trim() === comparisonValue?.trim() : filter?.value === comparisonValue));
      });
  }

  gridAction(event: any) {
    const { button, selectedCheckBox } = event;

    if (!this.isFilterValid(this.timesheetFilterChildComponent.timesheetSearchForm.value, this.appliedFilters)) {
      const errorMessage = `Some filters are newly applied but the search is not performed. Kindly perform search with updated filter or remove the filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox && selectedCheckBox.length === 0
      && (button.buttonAction === 'adjustTimesheet' || button.buttonAction === 'voidTimesheet')) {
      const errorMessage = `Kindly select checkbox and then perform this action.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox.length > 1 && (button.buttonAction === 'adjustTimesheet' || button.buttonAction === 'voidTimesheet')) {
      const errorMessage = `Adjust / Void operation can be performed on single timesheet at a time.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (button.buttonAction === 'adjustTimesheet') {
      const selectedServiceCode = selectedCheckBox[0]?.serviceCodeLkp ?? selectedCheckBox[0]?.serviceDescription;
      if (this.penaltyServiceCodes.includes(selectedServiceCode) || selectedServiceCode.includes('OTE')) {
        const errorMessage = `CA_MEAL_PENALTY and REST_PENALTY, OTE service codes cannot be adjusted. These can only be voided.`;
        this.errorAlertService.openModal('information', 'Information', errorMessage);
        return;
      }
    }

    if (button) {
      switch (button.buttonAction) {
        case 'adjustTimesheet': {
          const selectedTimesheet = selectedCheckBox[0];
          const selectedService = selectedTimesheet.serviceCodeDetailId != null
            ? this.serviceCodes?.find(service => service.lkpValueCode === selectedTimesheet.serviceCodeDetailId.toString())
            : undefined;
          this.timesheetData = {
            timesheetLineId: selectedTimesheet.timesheetLineId,
            serviceDate: selectedTimesheet.serviceDate,
            serviceCode: selectedService,
            authDesc: selectedTimesheet.authDesc,
            clockInLoc: selectedTimesheet.clockInLoc,
            clockOutLoc: selectedTimesheet.clockOutLoc,
            timeIn: selectedTimesheet.timeIn,
            timeOut: selectedTimesheet.timeOut,
            billedUnits: selectedTimesheet.billedUnits,
            providerPplID: selectedTimesheet.providerPplID,
            participantId: selectedTimesheet.participantId,
            providerId: selectedTimesheet.providerId
          };
          break;
        }
        case 'voidTimesheet': {
          const selectedTimesheet = selectedCheckBox[0];
          this.timesheetData = {
            timesheetLineId: selectedTimesheet.timesheetLineId
          };
          break;
        }
        // case 'adjustAllTimesheet': {
        //   if (selectedCheckBox.length > 0) {
        //     this.timesheetData = {
        //       timesheetLineIds: selectedCheckBox.map((entry: any) => entry.timesheetLineId)
        //     }
        //   }
        //   break;
        // }
        // case 'voidAllTimesheets': {
        //   if (selectedCheckBox.length > 0) {
        //     this.timesheetData = {
        //       timesheetLineIds: selectedCheckBox.map((entry: any) => entry.timesheetLineId)
        //     }
        //   }
        //   break;
        // }
      }

      this.selectedAction = button;

      const dialogConfig: DialogConfig = {
        title: `Timesheet ${button.button_label}`,
        size: 'medium',
        position: 'top',
        showClose: true,
        showButtons: false,
        styleClass: this.selectedAction?.buttonAction !== 'adjustTimesheet' ? 'limit-dialog-width' : ''
      };

      this.adjustVoidDialog = this.dialogSvc.openDialog(
        this.timesheetAdjustVoidDialog,
        dialogConfig,
        () => this.handleCancel(),
        () => this.handleCancel()
      );
    }
  }

  closeAdjustVoidDialog() {
    this.timesheetData = {};
    if (this.adjustVoidDialog) {
      this.dialogSvc.closeDialog(this.adjustVoidDialog);
      this.adjustVoidDialog = null;
    }
  }

  handleCancel() {
    this.closeAdjustVoidDialog();
  }

  timesheetAdjustedOrVoided(event: any) {
    this.closeAdjustVoidDialog();
    // Clear the grid's checkbox selection so the just-actioned row doesn't carry
    // over and combine with the next selection (which would trip the
    // "single timesheet at a time" guard on the following Adjust/Void).
    if (this.childComponent) {
      this.childComponent.selectedCheckBoxValue = [];
    }
    this.searchTimesheets(this.appliedFilters, event.startingPageNumber, event?.first ?? 0);
  }

  onPageLoad(event: any) {
    this.searchTimesheets(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, event.rows ?? 20);
  }

  onCellClicked(event: any) {
    const col = event?.col;
    const rowData = event?.rowData;
    if (col?.field === 'tasAuthorizationDetailId' && rowData?.tasAuthorizationDetailId) {
      this.openAuthDialog(rowData);
    }
  }

  openAuthDialog(rowData: any) {
    this.selectedAuthorizationId = rowData.tasAuthorizationDetailId;
    const dialogConfig: DialogConfig = {
      title: `Auth Details for ${rowData.tasAuthorizationDetailId}`,
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };
    this.dialogSvc.openDialog(this.authDialog, dialogConfig, () => { }, () => { });
  }
}
